import { create } from 'zustand';

const STORE_VERSION = 2; // Increment this when schema changes

// Helper to get initial state from localStorage
const getInitialState = () => {
  if (typeof window === 'undefined') {
    return {
      version: STORE_VERSION,
      moduleId: null,
      moduleUuid: null,
      moduleName: null,
      courseId: null,
      courseUuid: null,
      courseName: null,
      lessons: [],
      totalLessons: 0,
      totalCompletedLessons: 0
    };
  }

  try {
    const stored = localStorage.getItem('module-store');
    if (stored) {
      const parsed = JSON.parse(stored);

      // Clear old cache if version doesn't match
      if (parsed.version !== STORE_VERSION) {
        localStorage.removeItem('module-store');
        return {
          version: STORE_VERSION,
          moduleId: null,
          moduleUuid: null,
          moduleName: null,
          courseId: null,
          courseUuid: null,
          courseName: null,
          lessons: [],
          totalLessons: 0,
          totalCompletedLessons: 0
        };
      }

      return {
        version: STORE_VERSION,
        moduleId: parsed.moduleId || null,
        moduleUuid: parsed.moduleUuid || null,
        moduleName: parsed.moduleName || null,
        courseId: parsed.courseId || null,
        courseUuid: parsed.courseUuid || null,
        courseName: parsed.courseName || null,
        lessons: [],
        totalLessons: 0,
        totalCompletedLessons: 0
      };
    }
  } catch (e) {
    // Error reading from localStorage, clear it
    localStorage.removeItem('module-store');
  }

  return {
    version: STORE_VERSION,
    moduleId: null,
    moduleUuid: null,
    moduleName: null,
    courseId: null,
    courseUuid: null,
    courseName: null,
    lessons: [],
    totalLessons: 0,
    totalCompletedLessons: 0
  };
};

const initialState = getInitialState();

const useModuleStore = create((set) => ({
  ...initialState,
  removeLessons: () => set((state) => ({
    ...initialState,
    courseId: state.courseId,
    courseUuid: state.courseUuid,
    courseName: state.courseName,
    moduleId: state.moduleId,
    moduleUuid: state.moduleUuid,
    moduleName: state.moduleName
  })),
  removeModuleId: () => {
    set({ moduleId: null, moduleUuid: null, moduleName: null });
    // Update localStorage
    if (typeof window !== 'undefined') {
      try {
        const current = JSON.parse(localStorage.getItem('module-store') || '{}');
        localStorage.setItem('module-store', JSON.stringify({
          ...current,
          version: STORE_VERSION,
          moduleId: null,
          moduleUuid: null,
          moduleName: null
        }));
          } catch (e) {
            // Error updating localStorage
          }
    }
  },

  // Atomically clear lesson-related state when switching to a different
  // module. Without this, callers (ModuleCard's onClick, Details.jsx's sync
  // effect) can leave the store in an inconsistent state where moduleId
  // matches the new module but `lessons` still belongs to the previous one,
  // which leaks the previous module's lessons into the new module's UI.
  setModuleId: (id) => set((state) => {
    if (state.moduleId === id) return state;
    return {
      moduleId: id,
      lessons: [],
      totalLessons: 0,
      totalCompletedLessons: 0
    };
  }),
  setModuleInfo: (moduleId, moduleUuid, moduleName) => {
    set((state) => {
      if (state.moduleId === moduleId) {
        return { moduleId, moduleUuid, moduleName };
      }
      return {
        moduleId,
        moduleUuid,
        moduleName,
        lessons: [],
        totalLessons: 0,
        totalCompletedLessons: 0
      };
    });
    // Persist to localStorage
    if (typeof window !== 'undefined') {
      try {
        const current = JSON.parse(localStorage.getItem('module-store') || '{}');
        localStorage.setItem('module-store', JSON.stringify({
          ...current,
          version: STORE_VERSION,
          moduleId,
          moduleUuid,
          moduleName
        }));
          } catch (e) {
            // Error saving to localStorage
          }
        }
      },
      setCourseInfo: (courseId, courseUuid, courseName) => {
        set({ courseId, courseUuid, courseName });
        // Persist to localStorage
        if (typeof window !== 'undefined') {
          try {
            const current = JSON.parse(localStorage.getItem('module-store') || '{}');
            localStorage.setItem('module-store', JSON.stringify({
              ...current,
              version: STORE_VERSION,
              courseId,
              courseUuid,
              courseName
            }));
          } catch (e) {
            // Error saving to localStorage
          }
        }
      },
  setLessons: (lessons) => set({ lessons }),
  setTotalLessons: (total) => set({ totalLessons: total }),
  setTotalCompletedLessons: (total) =>
    set({ totalCompletedLessons: total }),

      updateLessonBySlug: (slug, completed) =>
        set((state) => ({
          lessons: state.lessons.map((lesson) =>
            lesson.slug === slug
              ? {
                  ...lesson,
                  has_current_user_completed: completed
                }
              : lesson
          ),
          totalCompletedLessons: state.totalCompletedLessons + 1
        }))
}));

export default useModuleStore;
